import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DemoProvider, useDemo } from "@/context/DemoContext";
import {
  DEMO_PROPERTIES, DEMO_COVERAGE, DEMO_PROOF_PAIRS, DEMO_STORYBOARD, DEMO_DOSSIER, DEMO_SECTIONS, DEMO_CONDITION_HISTORY, DEMO_TIMELINE, DEMO_INSPECTION,
} from "@/lib/demoData";
import { ConditionHistoryView } from "@/components/ConditionHistoryView";
import { TenancyTimelineView } from "@/components/TenancyTimelineView";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldAlert, LogOut, ChevronLeft, ChevronRight, Sparkles, Lock, Camera, ScanEye,
  GitCompareArrows, Layers, FileText, Check, X, Pencil, AlertTriangle, ImageOff, History, Clock, CalendarClock, Mail, MessageSquarePlus, CheckCircle2,
} from "lucide-react";

const PLUS_BADGE = (
  <span data-testid="demo-plus-badge" className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/20">
    <Sparkles size={10} /> Sample of a TenantSeal Plus feature
  </span>
);

// Licensed sample image with a persistent SAMPLE watermark; falls back to a clear placeholder.
function SampleImg({ src, alt, className = "", style, "data-testid": testid }) {
  const [err, setErr] = useState(false);
  if (err) return (
    <div className={`grid place-items-center bg-slate-100 text-[#94A3B8] ${className}`} style={style} data-testid={testid || "demo-img-error"}>
      <div className="text-center"><ImageOff size={18} className="mx-auto" /><span className="text-[9px]">Sample image</span></div>
    </div>
  );
  return (
    <div className={`relative overflow-hidden ${className}`} style={style} data-testid={testid}>
      <img src={src} alt={alt} onError={() => setErr(true)} className="h-full w-full object-cover" />
      <span className="absolute bottom-1 right-1 text-[8px] font-bold tracking-widest text-white/90 bg-black/45 px-1.5 py-0.5 rounded">SAMPLE</span>
    </div>
  );
}

const QLABEL = { too_dark: "Darkness", unsealed: "Unsealed", label_confirmation_needed: "Label to confirm", missing_pair: "Missing pair", blurry: "Blur", glare: "Glare" };

function DemoShell() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [roDialog, setRoDialog] = useState(false);
  const current = params.get("section") || "coverage";
  const idx = Math.max(0, DEMO_SECTIONS.findIndex((s) => s.key === current));

  useEffect(() => { window.scrollTo(0, 0); }, [current]);

  const goto = (key) => setParams({ section: key });
  const exit = () => navigate("/dashboard");
  const readOnly = () => setRoDialog(true);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-28" data-testid="demo-mode-page">
      {/* Persistent, undismissable SAMPLE banner (visible without scrolling) */}
      <div className="sticky top-0 z-30 bg-amber-500 text-white" data-testid="demo-banner">
        <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-2">
          <ShieldAlert size={18} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-extrabold leading-tight tracking-wide">SAMPLE WORKSPACE — NOT REAL EVIDENCE</p>
            <p className="text-[10px] leading-tight text-amber-50 hidden xs:block">Sample actions are not saved and cannot create, verify, share or export genuine evidence.</p>
          </div>
          <Button data-testid="demo-exit-btn" onClick={exit} size="sm" className="h-8 rounded-lg bg-white/95 hover:bg-white text-amber-700 text-xs font-bold shrink-0">
            <LogOut size={13} className="mr-1" /> Exit sample
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-3">
        <p className="text-[11px] text-[#64748B] leading-snug" data-testid="demo-subnote">
          You can explore TenantSeal’s tools here. Sample actions are not saved and cannot create, verify, share or export genuine evidence.
        </p>

        {/* Guided progress */}
        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-navy" data-testid="demo-progress">{idx + 1} of {DEMO_SECTIONS.length} — {DEMO_SECTIONS[idx].label}</p>
          <div className="flex gap-1.5">
            <button data-testid="demo-prev" disabled={idx === 0} onClick={() => goto(DEMO_SECTIONS[idx - 1].key)}
              className="h-8 w-8 grid place-items-center rounded-lg border border-[#E2E8F0] bg-white text-navy disabled:opacity-40"><ChevronLeft size={16} /></button>
            <button data-testid="demo-next" disabled={idx === DEMO_SECTIONS.length - 1} onClick={() => goto(DEMO_SECTIONS[idx + 1].key)}
              className="h-8 w-8 grid place-items-center rounded-lg border border-[#E2E8F0] bg-white text-navy disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1" data-testid="demo-nav">
          {DEMO_SECTIONS.map((s, i) => (
            <button key={s.key} data-testid={`demo-nav-${s.key}`} onClick={() => goto(s.key)}
              className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1.5 rounded-full border ${current === s.key ? "bg-navy text-white border-navy" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>
              {i + 1}. {s.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {current === "coverage" && <CoverageSection />}
          {current === "proof-replay" && <ProofReplaySection onWrite={readOnly} />}
          {current === "self-audit" && <SelfAuditSection onWrite={readOnly} />}
          {current === "storyboard" && <StoryboardSection onWrite={readOnly} />}
          {current === "dossier" && <DossierSection onWrite={readOnly} />}
          {current === "condition-history" && <ConditionHistorySection onWrite={readOnly} />}
          {current === "timeline" && <TimelineSection onWrite={readOnly} />}
          {current === "inspection" && <InspectionSection onWrite={readOnly} />}
        </div>

        <p className="mt-6 mb-2 text-center text-[10px] text-[#94A3B8]" data-testid="demo-image-credit">
          Sample photos bundled locally · displayed with a SAMPLE watermark · not real evidence
        </p>
      </div>

      {/* Consistent friendly read-only explanation for any mutating control */}
      <Dialog open={roDialog} onOpenChange={setRoDialog}>
        <DialogContent data-testid="demo-readonly-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock size={16} className="text-amber-500" /> Read-only sample</DialogTitle>
            <DialogDescription>This is a read-only sample. Changes are not saved and cannot create evidence.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button data-testid="demo-continue-btn" variant="outline" onClick={() => setRoDialog(false)}>Continue exploring</Button>
            <Button data-testid="demo-exit-try-btn" onClick={() => navigate("/start")} className="bg-navy text-white">Exit sample and try with my property</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-[#E2E8F0] bg-white p-4 mb-4">{children}</div>
);
const SectionHead = ({ icon: Icon, title, plus }) => (
  <div className="flex items-center justify-between gap-2 mb-1">
    <h2 className="font-heading text-lg font-bold text-navy flex items-center gap-1.5"><Icon size={17} className="text-brand" /> {title}</h2>
    {plus && PLUS_BADGE}
  </div>
);

// ---- Section 1: Coverage ----
function CoverageSection() {
  const c = DEMO_COVERAGE;
  return (
    <div data-testid="demo-coverage">
      <Card testid="demo-coverage-summary">
        <SectionHead icon={ScanEye} title="Evidence Coverage Map" />
        <p className="font-heading text-2xl font-bold text-navy" data-testid="demo-coverage-headline">{c.summary.percentage}% documented</p>
        <p className="text-sm text-[#475569]">{c.summary.captured_views} of {c.summary.applicable_views} recommended views captured · {c.summary.rooms_complete} room complete · {c.summary.rooms_needing_attention} need attention</p>
      </Card>
      {c.rooms.map((r) => (
        <Card key={r.room_id} testid={`demo-coverage-room-${r.room_id}`}>
          <div className="flex items-center justify-between">
            <p className="font-semibold text-navy text-sm">{r.room_name}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${r.status === "complete" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              {r.status === "complete" ? "Complete" : "Needs attention"} · {r.captured_slots}/{r.applicable_slots}
            </span>
          </div>
          <div className="mt-2 space-y-1">
            {r.slots.map((s) => (
              <div key={s.slot} className="flex items-center justify-between text-xs">
                <span className="text-[#334155] flex items-center gap-1">
                  {s.state === "captured" ? <Check size={12} className="text-emerald-500" /> : <X size={12} className="text-[#CBD5E1]" />} {s.label}
                </span>
                <span className="flex gap-1">
                  {s.flags.map((f) => <span key={f} className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">{QLABEL[f] || f}</span>)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      ))}
      <Card testid="demo-coverage-actions">
        <p className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] mb-1.5">Suggested next steps</p>
        <ul className="space-y-1 text-xs text-[#334155] list-disc pl-4">{c.actions.map((a) => <li key={a}>{a}</li>)}</ul>
      </Card>
    </div>
  );
}

// ---- Section 2: Proof Replay ----
function ProofReplaySection({ onWrite }) {
  const [active, setActive] = useState(DEMO_PROOF_PAIRS[0].pair_id);
  return (
    <div data-testid="demo-proof-replay">
      <Card>
        <SectionHead icon={GitCompareArrows} title="Proof Replay" plus />
        <p className="text-sm text-[#475569]">Compare your sealed move-in baseline with the move-out photo, side by side.</p>
        <div className="mt-2 flex gap-1.5">
          {DEMO_PROOF_PAIRS.map((p) => (
            <button key={p.pair_id} data-testid={`demo-pair-tab-${p.pair_id}`} onClick={() => setActive(p.pair_id)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${active === p.pair_id ? "bg-navy text-white border-navy" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>{p.room_name}</button>
          ))}
        </div>
      </Card>
      {DEMO_PROOF_PAIRS.filter((p) => p.pair_id === active).map((p) => (
        <Card key={p.pair_id} testid={`demo-proof-pair-${p.pair_id}`}>
          <div className="grid grid-cols-2 gap-2">
            <div><p className="text-[10px] font-semibold text-[#94A3B8] mb-1">Move-in · sealed</p><SampleImg src={p.move_in_image} alt="move-in" className="aspect-square rounded-lg border border-[#E2E8F0]" /><p className="text-[9px] text-[#94A3B8] mt-0.5">{p.move_in_date}</p></div>
            <div><p className="text-[10px] font-semibold text-[#94A3B8] mb-1">Move-out{p.move_out_sealed ? " · sealed" : ""}</p><SampleImg src={p.move_out_image} alt="move-out" className="aspect-square rounded-lg border border-[#E2E8F0]" /><p className="text-[9px] text-[#94A3B8] mt-0.5">{p.move_out_date}</p></div>
          </div>
          {!p.move_out_sealed && <p className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"><AlertTriangle size={10} /> Move-out photo not sealed yet</p>}
          <p className={`mt-2 text-xs ${p.comparable ? "text-[#475569]" : "text-amber-700"}`}>{p.comparable ? "Comparable pair." : "Comparison limited."} {p.note}</p>
          <div className="mt-3 flex gap-2">
            <Button data-testid={`demo-retake-${p.pair_id}`} size="sm" variant="outline" onClick={onWrite} className="h-8 rounded-lg text-xs"><Camera size={13} className="mr-1" /> Retake</Button>
            <Button data-testid={`demo-seal-${p.pair_id}`} size="sm" variant="outline" onClick={onWrite} className="h-8 rounded-lg text-xs"><Lock size={13} className="mr-1" /> Seal</Button>
          </div>
        </Card>
      ))}
      <SameViewDemoCard pair={DEMO_PROOF_PAIRS.find((p) => p.pair_id === active) || DEMO_PROOF_PAIRS[0]} />
    </div>
  );
}

// Frontend-only SAMPLE Same-View Capture Guide (Feature 16) — no camera, no backend, no upload.
// Reuses the bundled local move-in/move-out pair to demonstrate the translucent overlay + opacity.
function SameViewDemoCard({ pair }) {
  const [opacity, setOpacity] = useState(45);
  const [overlayOn, setOverlayOn] = useState(true);
  return (
    <Card testid="demo-same-view">
      <div className="flex items-center gap-2">
        <Layers size={16} className="text-brand" />
        <h3 className="font-heading text-base font-semibold text-navy">Same-View Capture Guide</h3>
      </div>
      <p className="mt-1 text-xs text-[#475569]">Use your move-in photo as a translucent overlay while framing the move-out shot. Visual guide only — TenantSeal does not verify the angle is an exact match.</p>
      <p className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="demo-same-view-notice">SAMPLE capture guide · no photo will be taken</p>
      <div className="mt-3 relative aspect-[3/4] max-w-[240px] mx-auto rounded-xl overflow-hidden border border-[#E2E8F0] bg-black">
        <SampleImg src={pair.move_out_image} alt="simulated frame" className="absolute inset-0 w-full h-full" />
        {overlayOn && (
          <SampleImg src={pair.move_in_image} alt="move-in reference overlay" data-testid="demo-same-view-overlay"
            className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: opacity / 100 }} />
        )}
        <span className="absolute top-1.5 left-1.5 text-[9px] font-bold bg-black/55 text-white rounded px-1.5 py-0.5">SAMPLE — simulated frame</span>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button data-testid="demo-same-view-toggle" onClick={() => setOverlayOn((v) => !v)}
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${overlayOn ? "bg-navy text-white border-navy" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>{overlayOn ? "Overlay on" : "Overlay off"}</button>
        <span className="text-[11px] text-[#94A3B8]">Opacity</span>
        <input data-testid="demo-same-view-opacity" type="range" min="10" max="80" value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} className="flex-1 accent-brand" />
        <span className="text-[11px] text-[#94A3B8] w-8 text-right">{opacity}%</span>
      </div>
    </Card>
  );
}

// ---- Section 3: Self-Audit (temporary local-only interactions) ----
function SelfAuditSection({ onWrite }) {
  const { audit, confirmSuggestion, dismissSuggestion, editSuggestion } = useDemo();
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  return (
    <div data-testid="demo-self-audit">
      <Card>
        <SectionHead icon={ScanEye} title="Pre-Move-Out Self-Audit" plus />
        <p className="text-sm font-semibold text-navy" data-testid="demo-self-audit-progress">Reviewed {audit.counts.reviewed} of {audit.counts.confirmed_pairs} confirmed pairs</p>
        <p className="mt-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="demo-self-audit-notice">Demo changes are not saved.</p>
      </Card>
      {audit.pairs.map((p) => (
        <Card key={p.pair_id} testid={`demo-audit-pair-${p.pair_id}`}>
          <p className="font-semibold text-navy text-sm">{p.room_name}</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <SampleImg src={p.move_in_image} alt="in" className="aspect-square rounded-lg border border-[#E2E8F0]" />
            <SampleImg src={p.move_out_image} alt="out" className="aspect-square rounded-lg border border-[#E2E8F0]" />
          </div>
          {p.quality_warnings.length > 0 && <div className="mt-2 flex gap-1">{p.quality_warnings.map((f) => <span key={f} className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{QLABEL[f] || f}</span>)}</div>}
          {/* AI draft */}
          <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-blue-700 flex items-center gap-1"><Sparkles size={11} /> AI suggestion — draft</p>
            {p.ai_draft.comparable === false ? (
              <p className="mt-1.5 text-xs text-[#475569]" data-testid={`demo-audit-limited-${p.pair_id}`}>{p.ai_draft.limited ? "Comparison limited. " : ""}{p.ai_draft.reason}</p>
            ) : (
              <div className="mt-2 space-y-2">
                {p.ai_draft.observations.map((o, i) => o.status === "dismissed" ? (
                  <p key={i} className="text-[11px] text-[#94A3B8] italic" data-testid={`demo-audit-dismissed-${p.pair_id}-${i}`}>Suggestion dismissed.</p>
                ) : o.status === "confirmed" ? (
                  <p key={i} className="text-[11px] text-emerald-700" data-testid={`demo-audit-confirmed-flag-${p.pair_id}-${i}`}><Check size={11} className="inline" /> Confirmed as your observation.</p>
                ) : (
                  <div key={i} className="rounded-lg border border-blue-100 bg-white p-2" data-testid={`demo-audit-suggestion-${p.pair_id}-${i}`}>
                    <p className="text-[10px] font-semibold text-[#94A3B8]">{o.region}</p>
                    {editing === `${p.pair_id}-${i}` ? (
                      <>
                        <textarea data-testid={`demo-audit-edit-input-${p.pair_id}-${i}`} value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className="mt-1 w-full text-xs border border-[#E2E8F0] rounded-lg p-2" />
                        <div className="mt-1 flex gap-1.5">
                          <button data-testid={`demo-audit-edit-save-${p.pair_id}-${i}`} onClick={() => { editSuggestion(p.pair_id, i, editText); setEditing(null); }} className="text-[11px] font-semibold text-white bg-navy rounded px-2 py-1">Save</button>
                          <button onClick={() => setEditing(null)} className="text-[11px] text-[#64748B] px-2 py-1">Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="mt-0.5 text-xs text-[#334155]">{o.description}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <button data-testid={`demo-audit-confirm-${p.pair_id}-${i}`} onClick={() => confirmSuggestion(p.pair_id, i)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"><Check size={11} /> Confirm</button>
                          <button data-testid={`demo-audit-edit-${p.pair_id}-${i}`} onClick={() => { setEditing(`${p.pair_id}-${i}`); setEditText(o.description); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#475569] bg-slate-50 border border-slate-200 rounded px-2 py-1"><Pencil size={11} /> Edit</button>
                          <button data-testid={`demo-audit-reject-${p.pair_id}-${i}`} onClick={() => dismissSuggestion(p.pair_id, i)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#94A3B8] bg-slate-50 border border-slate-200 rounded px-2 py-1"><X size={11} /> Reject</button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {p.confirmed_observations.length > 0 && (
            <div className="mt-2 space-y-1.5" data-testid={`demo-audit-confirmed-${p.pair_id}`}>
              {p.confirmed_observations.map((o) => (
                <div key={o.obs_id} className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-2">
                  <p className="text-[9px] font-bold uppercase tracking-wide text-emerald-700">{o.source === "user" ? "Your observation" : "Confirmed from AI suggestion"}</p>
                  <p className="text-xs text-[#334155] mt-0.5">{o.text}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// ---- Section 4: Charge Storyboard ----
function StoryboardSection({ onWrite }) {
  return (
    <div data-testid="demo-storyboard">
      <Card><SectionHead icon={Layers} title="Charge Storyboard" plus /><p className="text-sm text-[#475569]">Fictional landlord charges mapped to your sample exhibits. TenantSeal preserves the text — it does not judge whether a charge is fair or valid.</p></Card>
      {DEMO_STORYBOARD.charges.map((ch) => (
        <Card key={ch.charge_id} testid={`demo-charge-${ch.charge_id}`}>
          <div className="flex items-center justify-between">
            <p className="font-semibold text-navy text-sm">{ch.title}</p>
            <span className="text-sm font-bold text-navy">${ch.amount}</span>
          </div>
          <span className="text-[10px] font-semibold text-brand">{ch.exhibit_ref}</span>
          <div className="mt-2 flex gap-2">{ch.photo_refs.map((src, i) => <SampleImg key={i} src={src} alt="ref" className="h-16 w-16 rounded-lg border border-[#E2E8F0]" />)}</div>
          <p className="mt-2 text-xs text-[#475569]"><span className="font-semibold text-navy">Your note:</span> {ch.tenant_note}</p>
          <Button data-testid={`demo-charge-attach-${ch.charge_id}`} size="sm" variant="outline" onClick={onWrite} className="mt-2 h-8 rounded-lg text-xs">Attach photo</Button>
        </Card>
      ))}
    </div>
  );
}

// ---- Section 5: Exhibit Dossier ----
function DossierSection({ onWrite }) {
  return (
    <div data-testid="demo-dossier">
      <Card>
        <SectionHead icon={FileText} title="Exhibit Dossier" plus />
        <p className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="demo-dossier-notice">SAMPLE — NOT EVIDENCE</p>
        <p className="mt-2 text-sm text-[#475569]">A reader-first outline of how your exhibits would be organized. {DEMO_DOSSIER.storyboard_placement}</p>
        <Button data-testid="demo-dossier-export" size="sm" variant="outline" onClick={onWrite} className="mt-2 h-8 rounded-lg text-xs"><FileText size={13} className="mr-1" /> Export PDF</Button>
      </Card>
      {DEMO_DOSSIER.exhibits.map((e) => (
        <Card key={e.no} testid={`demo-exhibit-${e.no}`}>
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-7 w-7 rounded-lg bg-navy text-white font-bold text-xs shrink-0">{e.no}</span>
            <p className="font-semibold text-navy text-sm">{e.title}</p>
          </div>
          <div className="mt-2 flex gap-2">{e.images.map((src, i) => <SampleImg key={i} src={src} alt="exhibit" className="h-16 w-16 rounded-lg border border-[#E2E8F0]" />)}</div>
          <p className="mt-1.5 text-[10px] text-[#94A3B8]">Provenance: {e.provenance}</p>
        </Card>
      ))}
    </div>
  );
}

// ---- Section 6: Condition History (frontend fixture; reuses the real presentational view) ----
function ConditionHistorySection({ onWrite }) {
  const d = DEMO_CONDITION_HISTORY;
  return (
    <div data-testid="demo-condition-history">
      <Card>
        <SectionHead icon={History} title="Condition History" plus />
        <p className="text-sm text-[#475569]">Your condition evidence in chronological order across move-in, during tenancy and move-out. TenantSeal organizes your evidence — it does not judge condition, damage, fault or outcome.</p>
        <p className="mt-2 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="demo-ch-notice">SAMPLE — NOT EVIDENCE</p>
      </Card>
      <Card testid="demo-ch-view-wrap">
        <ConditionHistoryView
          address={d.address}
          groups={d.groups}
          excludedCount={d.excluded_unconfirmed_count}
          linkedCount={d.linked_count}
          demo
          onOpenEvidence={onWrite}
          onOpenProofReplay={onWrite}
        />
      </Card>
    </div>
  );
}

// ---- Section 7: Tenancy Timeline (frontend fixture; reuses the real presentational view) ----
function TimelineSection({ onWrite }) {
  const d = DEMO_TIMELINE;
  return (
    <div data-testid="demo-timeline">
      <Card>
        <SectionHead icon={Clock} title="Tenancy Timeline" />
        <p className="text-sm text-[#475569]">A chronological record of what you documented across your tenancy, oldest to newest. Communications, rent and forwarding notices appear as entries only — their contents stay private.</p>
        <p className="mt-2 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="demo-tl-notice">SAMPLE — NOT EVIDENCE</p>
      </Card>
      <Card testid="demo-tl-view-wrap">
        <TenancyTimelineView
          address={d.address}
          events={d.events}
          linkedCount={(d.linked_property_ids || []).length}
          demo
          onOpenEvent={onWrite}
        />
      </Card>
    </div>
  );
}

// ---- Section 8: Pre-Move-Out Inspection Request (fixture only; never sends, no backend/Resend) ----
function InspectionSection({ onWrite }) {
  const d = DEMO_INSPECTION;
  const fmtDay = (iso) => { try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "2-digit", year: "numeric", timeZone: "UTC" }); } catch { return iso; } };
  return (
    <div data-testid="demo-inspection">
      <Card>
        <SectionHead icon={CalendarClock} title="Inspection Request" />
        <p className="text-sm text-[#475569]">Ask your landlord or property manager to schedule a walkthrough before move-out. You review it and press send — nothing sends automatically.</p>
        <p className="mt-2 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="demo-insp-notice">SAMPLE — no email will be sent</p>
      </Card>
      <Card testid="demo-insp-review">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">Review before sending</p>
        <p className="mt-2 text-xs font-semibold text-[#64748B]">To</p>
        <p className="text-sm font-semibold text-navy break-all" data-testid="demo-insp-recipient">{d.recipient_name} · {d.recipient_email}</p>
        <p className="mt-3 text-xs font-semibold text-[#64748B]">Subject</p>
        <p className="text-sm text-navy">{d.subject}</p>
        <p className="mt-3 text-xs font-semibold text-[#64748B]">Preferred availability</p>
        <ul className="mt-1 space-y-0.5 text-sm text-[#334155]">
          {d.windows.map((w, i) => <li key={i} data-testid={`demo-insp-window-${i}`}>- {fmtDay(w.date)}, {w.start}–{w.end}</li>)}
        </ul>
        <p className="mt-3 text-sm text-[#334155]">My recorded move-out date is {new Date(d.move_out_date + "T00:00:00Z").toLocaleDateString(undefined, { month: "long", day: "2-digit", year: "numeric", timeZone: "UTC" })}.</p>
        <p className="mt-2 text-sm text-[#334155]">{d.optional_message}</p>
      </Card>
      <Card testid="demo-insp-status">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border text-emerald-700 bg-emerald-50 border-emerald-200" data-testid="demo-insp-status-chip">
            <CheckCircle2 size={12} /> Request sent
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border text-emerald-700 bg-emerald-50 border-emerald-200">
            <Mail size={12} /> Delivered
          </span>
        </div>
        <p className="mt-2 text-[11px] text-[#94A3B8]">Delivered means the email provider reported delivery. It does not indicate agreement or a response.</p>
        <Button data-testid="demo-insp-log-response" size="sm" variant="outline" onClick={onWrite} className="mt-3 h-9 rounded-lg text-xs">
          <MessageSquarePlus size={13} className="mr-1" /> Log a response
        </Button>
      </Card>
    </div>
  );
}

export default function DemoMode() {
  return (
    <DemoProvider>
      <DemoShell />
    </DemoProvider>
  );
}
